import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { Container, Stack, Typography, Divider } from "@mui/material";

import {
  RegistroBackground,
  BackgroundOverlay,
  RegistroContainer,
  RegistroTextContainer,
  RegistroFormContainer,
  Logo,
  BrandHint,
  FormHeader,
  StyledTextField,
  PrimaryButton,
  SecondaryButton,
  ErrorText,
  ModalContentWrapper,
  ModalActionsRow,
} from "./RegisterStyle";

import logo from "../../assets/logo.png";
import backgroundImage from "../../assets/login/background-login.jpeg";

import { API_BASE_URL } from "../../utils/config";
import { AuthContext } from "../../context/AuthContext";
import Modal from "../../components/Modal/Modal";

function Register() {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const passwordMismatch = useMemo(() => {
    if (!password || !confirmPassword) return false;
    return password !== confirmPassword;
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}auth/register`, {
        nombre,
        apellido,
        email,
        password,
      });

      if (response?.data?.success) {
        setIsModalOpen(true);
      } else {
        setError("Error al registrarse. Por favor, intenta de nuevo.");
      }
    } catch (err) {
      setError(
          err.response?.data?.message ||
          "Error al registrarse. Por favor, intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => navigate("/login");

  const ModalContent = (
      <ModalContentWrapper>
        <Logo src={logo} alt="logo" />
        <Typography
            variant="h6"
            sx={{ fontWeight: 900, letterSpacing: -0.2, mt: 1 }}
        >
          Confirmación de cuenta
        </Typography>

        <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
          ¡Bienvenido! Enviamos un correo a{" "}
          <strong style={{ opacity: 0.95 }}>{email}</strong> con un enlace para
          activar tu cuenta.
        </Typography>

        <Typography variant="caption" sx={{ opacity: 0.7, mt: 1 }}>
          Si no lo ves, revisa tu bandeja de spam o promociones.
        </Typography>

        <ModalActionsRow>
          <SecondaryButton
              fullWidth
              variant="outlined"
              onClick={() => setIsModalOpen(false)}
          >
            Cerrar
          </SecondaryButton>
          <PrimaryButton fullWidth variant="contained" onClick={handleGoToLogin}>
            Iniciar sesión
          </PrimaryButton>
        </ModalActionsRow>
      </ModalContentWrapper>
  );

  return (
      <RegistroBackground bg={backgroundImage}>
        <BackgroundOverlay />

        <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 2, sm: 3 },
            }}
        >
          <RegistroContainer>
            {/* IZQUIERDA */}
            <RegistroTextContainer>
              <Logo src={logo} alt="Logo de la empresa" />
              <BrandHint>
                <span>Seguro</span>
                <span>•</span>
                <span>Simple</span>
                <span>•</span>
                <span>Rápido</span>
              </BrandHint>
            </RegistroTextContainer>

            {/* DERECHA */}
            <RegistroFormContainer>
              <FormHeader>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 900, letterSpacing: -0.3 }}
                >
                  Crea tu cuenta
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.78 }}>
                  Completa tus datos para registrarte.
                </Typography>
              </FormHeader>

              <Divider sx={{ width: "100%", my: 2.2, opacity: 0.35 }} />

              {error ? <ErrorText>{error}</ErrorText> : null}

              <form onSubmit={handleSubmit} noValidate style={{ width: "100%" }}>
                <Stack spacing={2} sx={{ width: "100%" }}>
                  <StyledTextField
                      fullWidth
                      label="Nombre"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      autoComplete="given-name"
                      required
                  />

                  <StyledTextField
                      fullWidth
                      label="Apellido"
                      value={apellido}
                      onChange={(e) => setApellido(e.target.value)}
                      autoComplete="family-name"
                      required
                  />

                  <StyledTextField
                      fullWidth
                      label="Correo"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                  />

                  <StyledTextField
                      fullWidth
                      label="Contraseña"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                  />

                  <StyledTextField
                      fullWidth
                      label="Confirmar contraseña"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      error={passwordMismatch}
                      helperText={passwordMismatch ? "Las contraseñas no coinciden." : " "}
                  />

                  <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.2}
                      sx={{ pt: 0.5 }}
                  >
                    <SecondaryButton
                        fullWidth
                        variant="outlined"
                        type="button"
                        onClick={handleGoToLogin}
                        disabled={loading}
                    >
                      Ya tengo cuenta
                    </SecondaryButton>

                    <PrimaryButton
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={loading}
                    >
                      {loading ? "Registrando..." : "Registrar"}
                    </PrimaryButton>
                  </Stack>
                </Stack>
              </form>
            </RegistroFormContainer>
          </RegistroContainer>
        </Container>

        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          {ModalContent}
        </Modal>
      </RegistroBackground>
  );
}

export default Register;
